import { device as deviceUtils } from '@seek/ca-myprofile-utils';

export class BrowserError extends Error {
  data?: Record<string, unknown>;

  constructor(message: string, data?: Record<string, unknown>) {
    super(message);
    this.name = 'BrowserError';
    this.data = data;
  }
}

export class ValidationError extends BrowserError {
  constructor(message: string, data?: Record<string, unknown>) {
    super(message, data);
    this.name = 'ValidationError';
  }
}

export class SystemError extends BrowserError {
  constructor(message: string, data?: Record<string, unknown>) {
    super(message, data);
    this.name = 'SystemError';
  }
}

/**
 * Validates an image file for avatar upload.
 * Checks file type and dimensions.
 *
 * @param file - The image file to validate
 * @throws {ValidationError} If file type is invalid or image is too small
 * @throws {SystemError} If file cannot be read or image cannot be loaded
 */
export const validateImage = async (file: File): Promise<void> => {
  const onSafari = deviceUtils.isSafari();
  // Validate file type
  const validTypes = onSafari
    ? ['image/jpeg', 'image/jpg', 'image/png', 'image/heic']
    : ['image/jpeg', 'image/jpg', 'image/png'];

  if (!validTypes.includes(file.type.toLowerCase())) {
    throw new ValidationError(
      `Invalid file type. Only JPEG/PNG${onSafari ? '/HEIC' : ''} images are allowed.`,
    );
  }

  // Validate image dimensions
  const img = new Image();
  let objectUrl: string;

  try {
    objectUrl = URL.createObjectURL(file);
  } catch {
    throw new SystemError('Failed to create object URL.');
  }

  await new Promise<void>((resolve, reject) => {
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);

      if (img.width < 1 || img.height < 1) {
        reject(
          new ValidationError('Image too small. Minimum size is 1×1 pixels.'),
        );
      } else {
        resolve();
      }
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      // Log context to narrow down "file is corrupted" reports: common causes are
      // MIME/content mismatch (file.type vs actual bytes), HEIC decode failures on Safari,
      // very large images, or truncated/corrupted bytes.
      const diagnostic = {
        fileType: file.type,
        fileSize: `${(file.size / 1024).toFixed(2)} KB`,
        file: file.name,
        browser: deviceUtils.getBrowserName(),
      };

      reject(new SystemError('Failed to load image.', diagnostic));
    };

    img.src = objectUrl;
  });
};

/**
 * Determines the error type from a caught error.
 *
 * @param error - The error to categorize
 * @returns 'validation' for user errors, 'system' for system errors
 */
export const getErrorType = (error: unknown): 'validation' | 'system' => {
  if (error instanceof Error && error.name === 'ValidationError') {
    return 'validation';
  }
  return 'system';
};
